import { useEffect, useMemo, useState } from 'react'
import CustomModal, { ModalBody, ModalHeader } from '../shared/CustomModal'
import * as actions from '@actions'
import cn from 'clsx'
import CircleSpinner from '../shared/CircleSpinner'
import { MdClose, MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import LeafletMap from '../shared/LeafletPlot/LeafletMap'
import { COLORS } from './canvasUtils'

const DEPARTURE_CARDINAL_OPTIONS = ['E', 'W']
const INITIAL_CARDINAL_OPTIONS = ['N', 'S', 'E', 'W']

const PlotterModalV2 = ({ open, modalHandler, runsheetName, runsheetId }) => {
    
    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedLegalDescription, setSelectedLegalDescription] = useState(null)
    const [selectedDocumentId, setSelectedDocumentId] = useState(null)
    const [convertUnits, setConvertUnits] = useState([])
    
    const plots = useMemo(() => documents?.flatMap(doc => doc.rdj[0].value.legalDescriptions.map(ld => ld.plot))
        .filter(Boolean) || [], [documents])
    
    const selectedDocument = useMemo(() =>
        documents.find(doc => doc.id === selectedDocumentId) || null
    , [selectedDocumentId, documents])
    
    const selectedPlot = useMemo(() => selectedLegalDescription?.plot || null
        , [selectedLegalDescription, selectedDocument])
    
    // get all legals that have plotted data
    const ldsInPlot = useMemo(() => {
        
        return selectedDocument?.rdj[0].value.legalDescriptions.filter(ld => !!ld.plot) || []
        
    }, [selectedDocument])
    
    const _adjustDistanceFeet = (leg, newValue) => {
        const multiplier = convertUnits.find(cu => cu.unitName === leg.distanceUnit)?.multiplier || 1
        
        return {
            ...leg,
            distance: newValue,
            distanceFeet: newValue * multiplier,
        }
    }
    
    const _mergeLeg = (leg, fieldToChange, newValue) =>
        fieldToChange === 'distance' ? _adjustDistanceFeet(leg, newValue) : { ...leg, [fieldToChange]: newValue }
    
    const changeLegValue = async (legId, fieldToChange, newValue) => {
        if (!legId || !fieldToChange || !newValue) {
            toast.error('Failed to change value!')
            return
        }
        
        // i must traverse the whole object up until the place of editing then update all parents
        const oldDocIdx = documents.findIndex(doc => doc.id === selectedDocumentId)
        
        const newDoc = structuredClone(documents[oldDocIdx])
        
        const ld = newDoc.rdj[0].value.legalDescriptions.find(ld => ld.id === selectedLegalDescription.id)
        const oldLegIdx = ld.plot.legs.findIndex(leg => leg.id === legId)
        const oldLeg = ld.plot.legs[oldLegIdx]
        
        ld.plot.legs[oldLegIdx] = _mergeLeg(oldLeg, fieldToChange, newValue)
        
        documents[oldDocIdx] = newDoc
        const newDocs = [...documents]
        
        setDocuments(newDocs)
        setSelectedLegalDescription(ld)
        
        await actions.updateLegValue(legId, fieldToChange, newValue, oldLeg.distanceUnit)
    }
    
    useEffect(() => {
        if (!documents.length && open && runsheetId) {
            setLoading(true)
            actions.getDocumentPlotsByRunsheet(runsheetId)
                .then(docs => {
                    
                    let colorIdx = 0
                    
                    docs.forEach(doc => doc.rdj[0].value.legalDescriptions.forEach(ld => {
                        if (ld.plot) {
                            ld.plot.color = COLORS[colorIdx % COLORS.length]
                            colorIdx++
                        }
                    }))
                    
                    setDocuments(docs)
                    setLoading(false)
                    setSelectedDocumentId(docs[0].id)
                })
            actions.getConvertUnits().then(data => {
                setConvertUnits(data)
            })
        }
    }, [documents, open, runsheetId])
    
    useEffect(() => {
        setSelectedLegalDescription(null)
    }, [selectedDocumentId])
    
    return (
        <CustomModal
            open={open}
            modalHandler={() => modalHandler()}
            className="min-w-[calc(100vw-48px)] h-[95vh] "
            center>
            <ModalHeader>
                Deed Plotter - {runsheetName}
            </ModalHeader>
            <ModalBody
                className="h-[calc(90vh-45px)] overflow-none">
                {loading ? (
                    <div className="flex flex-col gap-4 items-center justify-center pb-36">
                        <CircleSpinner />
                        <h3>Calculating Metes and Bounds</h3>
                    </div>
                ) :
                    <div className="flex flex-row gap-4 h-[80vh]">
                        <div className="flex flex-col gap-2 flex-1">
                            <div className="flex flex-row h-[300px] ">
                                <div>
                                    <h3>Document</h3>
                                    <div className="flex flex-col overflow-auto h-full">
                                        {documents.map(document => (
                                            <div
                                                className="flex flex-row justify-start items-center"
                                                key={document.id}
                                                onClick={() => setSelectedDocumentId(document.id)}>
                                                <label className={cn(
                                                    'label cursor-pointer hover:bg-base-100 rounded-l-lg p-2',
                                                    {
                                                        'bg-base-100'
                                                        : selectedDocumentId && selectedDocumentId === document.id,
                                                    },
                                                )}>
                                                    <span className="label-text w-[150px] truncate">
                                                        {document.name}
                                                    </span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="h-full w-full">
                                    <h3>Legals</h3>
                                    <div className="bg-base-100 rounded-r-lg rounded-l-lg overflow-auto h-full -ml-1
                                        p-4 flex flex-col gap-4">
                                        {selectedDocumentId && (
                                            ldsInPlot.map(ld =>
                                                (<div
                                                    className="flex w-full justify-center"
                                                    key={ld.id}>
                                                    <div
                                                        className={cn('h-full min-w-[10px] rounded-l-lg opacity-50', {
                                                            '!opacity-100': ld.id === selectedLegalDescription?.id,
                                                        })}
                                                        style={{ backgroundColor: ld.plot?.color }}>
                                                    </div>
                                                    <div className="bg-base-300/20 p-4 max-h-[150px]
                                                        overflow-auto">
                                                        <p >{ld.original}</p>
                                                    </div>
                                                    <div
                                                        className="cursor-pointer btn btn-ghost btn-square
                                                            btn-sm"
                                                        onClick={() => setSelectedLegalDescription(prev =>
                                                            prev?.id === ld.id ? null : ld)
                                                        }>
                                                        {ld.id === selectedLegalDescription?.id
                                                            ? <MdClose /> : <MdEdit />}
                                                    </div>
                                                </div>)))
                                        }
                                    </div>
                                </div>
                            </div>
                            {selectedLegalDescription && selectedPlot?.legs?.length ? (
                                <div className="mt-10 overflow-auto max-h-[calc(50vh)]">
                                    <table className="table-auto w-full border-collapse ">
                                        <thead className="sticky top-0 bg-base-200 h-[30px]">
                                            <tr>
                                                <th>Initial</th>
                                                <th>Angle</th>
                                                <th>Departure</th>
                                                <th>Distance</th>
                                                <th>Distance Unit</th>
                                                <th>Dist. in Feets</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedPlot.legs.map(plot => (
                                                <tr key={plot.id}>
                                                    <td className="px-2 ">
                                                        <select
                                                            className="select select-sm select-bordered w-full"
                                                            value={plot.initial || ''}
                                                            onChange={e => {
                                                                changeLegValue(plot.id, 'initial', e.target.value)
                                                            }}>
                                                            <option disabled value="">--</option>
                                                            {INITIAL_CARDINAL_OPTIONS.map(opt => (
                                                                <option key={opt} value={opt}>
                                                                    {opt}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 ">
                                                        <input
                                                            type="text"
                                                            className="input input-bordered w-full"
                                                            defaultValue={plot.angle || ''}
                                                            onBlur={e => {
                                                                changeLegValue(plot.id, 'angle', e.target.value)
                                                            }} />
                                                    </td>
                                                    <td className="px-2 ">
                                                        <select
                                                            className="select select-sm select-bordered w-full"
                                                            value={plot.departure || ''}
                                                            onChange={e => {
                                                                changeLegValue(plot.id, 'departure', e.target.value)
                                                            }}>
                                                            <option disabled value="">--</option>
                                                            {DEPARTURE_CARDINAL_OPTIONS.map(opt => (
                                                                <option key={opt} value={opt}>
                                                                    {opt}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td className="px-2 ">
                                                        <input
                                                            type="text"
                                                            className="input input-bordered input-sm w-full"
                                                            defaultValue={plot.distance || ''}
                                                            onBlur={e => {
                                                                changeLegValue(plot.id, 'distance', e.target.value)
                                                            }} />
                                                    </td>
                                                    <td className="px-2 ">
                                                        <input
                                                            type="text"
                                                            disabled
                                                            className="input input-bordered input-sm w-full"
                                                            value={plot.distanceUnit} />
                                                    </td>
                                                    <td className="px-2 ">
                                                        <input
                                                            type="text"
                                                            disabled
                                                            className="input input-bordered input-sm w-full"
                                                            value={
                                                                (plot.distance * (convertUnits.find(cu =>
                                                                    cu.unitName === plot.distanceUnit)?.multiplier || 1)
                                                                ).toFixed(2)
                                                            } />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                        <div className="flex-1">
                            <h3>Map</h3>
                            <LeafletMap plots={plots} selectedPlot={selectedPlot?.id} />
                        </div>
                    </div>
                }
            
            </ModalBody>
        </CustomModal>
    )
}

export default PlotterModalV2